// bodyHistoryHelper — резолв «индекс body в истории» для записи в фичи.
//
// Когда фича создаётся (fillet, shell, combine, moveCopy), она ссылается
// на runtime bodyId типа 'body_5'. После rebuild эти id меняются.
// Чтобы фича пережила rebuild, сохраняем ИНДЕКС body в порядке его
// появления — это работает для линейных пайплайнов «создал A, создал B,
// объединил» и переживает clearAll+rebuild.
//
// Также экспортируем хелпер для извлечения endpoints рёбер по edgeId —
// нужно для fillet/chamfer, чтобы при rebuild найти то же ребро в новом
// shape (где edge.hashCode другие).

import useCadStore from './store/useCadStore';

/**
 * Возвращает индекс bodyId в текущем bodyMeshes (порядок вставки).
 * Возвращает -1 если bodyId не найден.
 */
export function getBodyIndex(bodyId) {
    const ids = Array.from(useCadStore.getState().bodyMeshes.keys());
    return ids.indexOf(bodyId);
}


/**
 * Возвращает endpoints каждого ребра указанного body как массив
 * [{ a: [x,y,z], b: [x,y,z] }, ...] в порядке edgeIds.
 *
 * Это «геометрический id» ребра — пара мировых точек. При rebuild
 * worker может найти то же ребро в новом shape, сравнив endpoints
 * с допуском.
 *
 * edgeIdsList — массив hashCode рёбер которые нужно достать. Если не
 * передан — возвращает все рёбра body.
 */
export function getEdgeEndpointsForBody(bodyId, edgeIdsList = null) {
    const mesh = useCadStore.getState().bodyMeshes.get(bodyId);
    if (!mesh || !mesh.edges || !mesh.edgeIds || !mesh.edgeOffsets) return [];
    const result = [];
    for (let i = 0; i < mesh.edgeIds.length; i++) {
        const eid = mesh.edgeIds[i];
        if (edgeIdsList && !edgeIdsList.includes(eid)) continue;
        const startVert = mesh.edgeOffsets[i];
        const endVert = mesh.edgeOffsets[i + 1];
        if (endVert <= startVert) continue;
        // mesh.edges = Float32Array из чередующихся пар точек (start, end, start, end, ...)
        // Сегмент i содержит точки от индекса startVert до endVert-1.
        // Берём ПЕРВУЮ и ПОСЛЕДНЮЮ точку сегмента как endpoints ребра.
        const lastVert = endVert - 1;
        result.push({
            edgeId: eid,
            a: [
                mesh.edges[startVert * 3],
                mesh.edges[startVert * 3 + 1],
                mesh.edges[startVert * 3 + 2],
            ],
            b: [
                mesh.edges[lastVert * 3],
                mesh.edges[lastVert * 3 + 1],
                mesh.edges[lastVert * 3 + 2],
            ],
        });
    }
    return result;
}


/**
 * Возвращает УСТОЙЧИВЫЕ дескрипторы указанных рёбер тела как
 * [{ mid:[x,y,z], len:number }, ...] в порядке edgeIds.
 *
 * Дескриптор = средняя точка ребра + длина (посчитаны воркером из B-rep,
 * приходят в mesh как edgeMids/edgeLens). Именно это надо сохранять в фичу
 * fillet/chamfer: при rebuild воркер найдёт то же ребро по дескриптору,
 * не завися от нестабильного hashCode и от триангуляции.
 *
 * edgeIdsList — hashCode рёбер, которые нужно достать (обязателен).
 * Рёбра без валидного дескриптора (len < 0) пропускаются.
 */
export function getEdgeDescriptorsForBody(bodyId, edgeIdsList = null) {
    const mesh = useCadStore.getState().bodyMeshes.get(bodyId);
    if (!mesh || !mesh.edgeIds || !mesh.edgeMids || !mesh.edgeLens) return [];
    const result = [];
    for (let i = 0; i < mesh.edgeIds.length; i++) {
        const eid = mesh.edgeIds[i];
        if (edgeIdsList && !edgeIdsList.includes(eid)) continue;
        const len = mesh.edgeLens[i];
        if (!(len > 0)) continue; // -1 = дескриптор недоступен
        result.push({
            edgeId: eid,
            mid: [
                mesh.edgeMids[i * 3],
                mesh.edgeMids[i * 3 + 1],
                mesh.edgeMids[i * 3 + 2],
            ],
            len,
        });
    }
    return result;
}
